import Joi from '@hapi/joi';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Company } from '../../../../common/src/domain/company/companyTableSchema';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson, fetchPost } from '../../store/fetch';
import { IStoreState } from '../../store/IStoreState';
import { Content, EditPanelButtons, Panel, PanelButton, PanelBody, Topbar, TopbarCrumb, TopbarCrumbActive, TopbarLeft, Tray } from '../elements';
import { ValidatingEditPanelSaveButton, ValidatingFilterDropdownWithUrl, ValidatingForm } from '../validatingElements';

interface IAddExistingUserProps {
    location: { query: { companyId: string } };
}

export function AddExistingUser({
    location: {
        query: { companyId },
    },
}: IAddExistingUserProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const [company, setCompany] = useState<Company | undefined>();
    const userRole = useSelector((state: IStoreState) => state.authentication.role);

    useEffect(() => {
        void fetchJson(`/api/companies/${companyId}`, 'GET').then((result) => setCompany(result.json));
    }, [companyId]);

    const onSubmit = async ({ item: { userId } }: any) => {
        await fetchPost(`/api/users/${userId}/addCompany`, {}, { companyId });
        navigate(`/companies/${companyId}`);

        return { id: '' };
    };

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={t('common.addItem', t('common.user'))} icon="fa-user" />
                    <TopbarCrumb>
                        <Link to={`/companies/${companyId}`}>{company?.name}</Link>
                    </TopbarCrumb>
                    <TopbarCrumb>
                        <Link to="/companies">{t('common.companies')}</Link>
                    </TopbarCrumb>
                </TopbarLeft>
            </Topbar>

            <Content>
                <Tray>
                    <ValidatingForm schema={Joi.object().keys({ userId: Joi.string().required() })} overrideSubmit={onSubmit}>
                        <Panel small>
                            <PanelBody>
                                {userRole === 'administrator' && (
                                    // eslint-disable-next-line react/forbid-elements
                                    <div className="alert alert-warning" role="alert">
                                        <i className="fa fa-exclamation-triangle mr5"></i>
                                        {t('user.administratorAddExistingUserWarning')}
                                    </div>
                                )}
                                <ValidatingFilterDropdownWithUrl
                                    url="/api/users"
                                    name="userId"
                                    topLabel={t('user.existingUsers')}
                                    placeholder={t('common.selectItem', t('common.user'))}
                                    getOptionLabel={(item) => `${item.firstName} ${item.lastName} (${item.emailAddress})`}
                                    dataValueField="id"
                                    urlQueryParameters={{ excludeCompanyId: companyId, excludeAdminClientCompanies: 'true' }}
                                />
                            </PanelBody>

                            <EditPanelButtons footer>
                                <PanelButton text={t('common.cancel')} onClick={() => navigate(`/companies/${companyId}`)} />
                                <ValidatingEditPanelSaveButton />
                            </EditPanelButtons>
                        </Panel>
                    </ValidatingForm>
                </Tray>
            </Content>
        </>
    );
}
